import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { FaCookieBite } from 'react-icons/fa'; // Example icon

const ConsentBanner = () => {
    const { consentGiven, submitConsentDecision, isLoading } = useAuth();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false); // State to control animation visibility
    const [cookieDocument, setCookieDocument] = useState(null);

    const [policyLoading, setPolicyLoading] = useState(true);
    const [documentError, setDocumentError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const submitting = useRef(false);
    const [documentAttempt, setDocumentAttempt] = useState(0);
    useEffect(() => {
        let active = true;
        setPolicyLoading(true);
        setDocumentError(false);
        axios.get('/api/consent/document').then(({ data }) => {
            const found = data?.document;
            if (active) { setCookieDocument(found || null); setDocumentError(!found); setPolicyLoading(false); }
        }).catch(() => { if (active) { setCookieDocument(null); setDocumentError(true); setPolicyLoading(false); } });
        return () => { active = false; };
    }, [documentAttempt]);

    // Consistent styling. This banner is the first thing on screen on a first
    // visit and it sits over every page, so it follows the site palette —
    // it was blue-600/blue-400 on a slate background, the one loud off-brand
    // element on an otherwise black-and-moss site.
    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";
    const accentColor = "text-moss-light"; // For link
    const borderColor = "border-moss-deep/50";

    const saveChoice = useCallback(async (choice) => {
        if (submitting.current) return;
        submitting.current = true;
        setSaving(true);
        setSaveError('');
        try {
            const saved = await submitConsentDecision(choice);
            if (!saved) setSaveError('Your preference could not be saved. Please try again.');
        } catch {
            setSaveError('Your preference could not be saved. Please try again.');
        } finally { submitting.current = false; setSaving(false); }
    }, [submitConsentDecision]);

    // Effect to trigger visibility animation once loaded and consent is pending
    useEffect(() => {
        // Show the banner only if loading is complete and consent decision is still pending (null)
        if (!isLoading && consentGiven === null) {
            // Use a timeout to allow the component to mount before starting the transition
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 100); // Short delay
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false); // Hide if already decided or still loading
        }
    }, [isLoading, consentGiven]);

    // Don't render the container at all if consent is not pending or still loading initially
    // This prevents a flash of the component before the animation check
    if (consentGiven !== null || isLoading) {
        return null;
    }

    return (
        <div
            className={`fixed bottom-0 left-0 right-0 ${borderColor} border-t p-4 z-50 shadow-lg backdrop-blur-sm transition-transform duration-500 ease-out ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
            style={{ backgroundColor: 'rgba(15, 26, 18, 0.92)' }}
        >
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <FaCookieBite className={`${primaryTextColor} text-2xl hidden sm:block`} aria-hidden="true" />
                    <p className={`${secondaryTextColor} text-sm flex-grow`}>
                        {t('consent.banner.message', 'We use cookies and similar technologies to understand how you use our website and to improve your experience. This includes personalizing content and analytics. By clicking "Accept", you consent to the use of these technologies for these purposes.')}
                        {cookieDocument ? <a href={cookieDocument.documentUrl} target="_blank" rel="noreferrer" className={`ml-1 underline ${accentColor} hover:text-moss`}>Policy ({cookieDocument.documentVersion})</a> : null}
                    </p>
                </div>
                {policyLoading ? <p role="status" className="text-sm text-silver-light">Loading cookie preferences…</p> : null}
                {documentError ? <p role="alert" className="text-sm text-silver-light">Cookie preferences are temporarily unavailable. Optional analytics stay off. <button type="button" onClick={() => setDocumentAttempt(value => value + 1)} className="underline">Retry</button></p> : null}
                {saveError ? <p role="alert" className="text-sm text-silver-light">{saveError}</p> : null}
                <div className="flex-shrink-0 flex gap-3">
                    <button
                        onClick={() => saveChoice('reject')}
                        disabled={!cookieDocument || saving || policyLoading}
                        aria-busy={saving}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium ${secondaryTextColor} border border-gray-700 hover:border-silver-dark hover:text-silver-light transition duration-200 disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {t('consent.banner.reject', 'Reject')} {/* Add key to translation */}
                    </button>
                    <button
                        onClick={() => saveChoice('accept')}
                        disabled={!cookieDocument || saving || policyLoading}
                        aria-busy={saving}
                        className="px-4 py-1.5 rounded-md text-sm font-medium bg-moss text-black hover:bg-moss-light transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-moss-night focus:ring-moss-light disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? t('consent.banner.saving', 'Saving…') : t('consent.banner.accept', 'Accept')} {/* Add key to translation */}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentBanner;
